import React, { useState, useEffect } from "react";
import { BrowserRouter, Routes, Route, useNavigate, useLocation } from "react-router-dom";
import "./App.css";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Navigation Component - Simplified without Hamburger Menu
const Navigation = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const handleNameClick = () => {
    if (location.pathname !== '/') {
      navigate('/');
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }, 100);
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const scrollToSection = (sectionId) => {
    if (location.pathname !== '/') {
      navigate('/');
      setTimeout(() => {
        document.getElementById(sectionId)?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    } else {
      document.getElementById(sectionId)?.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-dark-navy/95 backdrop-blur-md border-b border-navy-700">
      <div className="container mx-auto px-4 py-4 flex justify-between items-center">
        <button onClick={handleNameClick} className="text-xl font-bold text-white hover:text-baby-blue transition-colors">
          Christopher Hanten
        </button>

        {location.pathname === '/' && (
          <div className="hidden md:flex space-x-8">
            <button onClick={() => scrollToSection('about')} className="nav-link">About</button>
            <button onClick={() => scrollToSection('skills')} className="nav-link">Skills</button>
            <button onClick={() => scrollToSection('experience')} className="nav-link">Experience</button>
            <button onClick={() => scrollToSection('projects')} className="nav-link">Projects</button>
            <button onClick={() => scrollToSection('contact')} className="nav-link">Contact</button>
          </div>
        )}
      </div>
    </nav>
  );
};

// Hero Section
const HeroSection = () => {
  const downloadResume = () => {
    const link = document.createElement('a');
    link.href = '/resume.pdf';
    link.download = 'Christopher_Hanten_Resume_2026.pdf';
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <section className="min-h-screen flex items-center justify-center bg-gradient-to-br from-dark-navy via-navy-900 to-navy-800 text-white relative overflow-hidden pt-24 md:pt-20">
      <div className="absolute inset-0 bg-stars opacity-20"></div>
      <div className="container mx-auto px-4 text-center relative z-10">
        <div className="mb-8 mt-8 md:mt-0">
          <div className="relative inline-block">
            {/* Glow effect behind image */}
            <div className="absolute inset-0 rounded-full bg-baby-blue/30 blur-xl scale-110"></div>
            <img 
              src="/profile.jpeg"
              alt="Christopher Hanten"
              loading="eager"
              className="relative w-48 h-48 rounded-full mx-auto mb-8 border-4 border-baby-blue shadow-2xl object-cover hover:scale-105 transition-transform duration-300"
              data-testid="hero-profile-image"
            />
          </div>
        </div>
        <h1
          className="text-5xl md:text-7xl font-bold mb-6 text-baby-blue"
          style={{ textShadow: '0 0 22px rgba(214, 222, 230, 0.55), 0 0 45px rgba(214, 222, 230, 0.30)' }}
        >
          Christopher Hanten
        </h1>
        <p className="text-xl md:text-2xl mb-4 text-baby-blue">
          Navy veteran | Senior Scrum Master
        </p>
        <p className="text-lg md:text-xl mb-12 font-semibold text-white border border-yellow-500 bg-baby-blue/10 px-4 py-2 rounded-lg inline-block max-w-4xl mx-auto leading-relaxed">
          Helping teams deliver complex work with clarity, discipline, and continuous improvement.
          <br/>
          Delivery leadership across enterprise and startup environments
        </p>
        <p className="text-lg md:text-xl mb-12 text-green-600 font-medium">
          Austin, Texas
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <button 
            onClick={downloadResume}
            className="bg-blue-300 hover:bg-blue-400 text-blue-900 font-semibold py-3 px-8 rounded-lg transition-all duration-300 hover:scale-105 shadow-lg"
          >
            📄 Resume
          </button>
          <a 
            href="https://www.linkedin.com/in/christopherhanten" 
            target="_blank" 
            rel="noopener noreferrer"
            className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-8 rounded-lg transition-all duration-300 hover:scale-105 shadow-lg inline-flex items-center justify-center"
          >
            <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M16.338 16.338H13.67V12.16c0-.995-.017-2.277-1.387-2.277-1.39 0-1.601 1.086-1.601 2.207v4.248H8.014v-8.59h2.559v1.174h.037c.356-.675 1.227-1.387 2.526-1.387 2.703 0 3.203 1.778 3.203 4.092v4.711zM5.005 6.575a1.548 1.548 0 11-.003-3.096 1.548 1.548 0 01.003 3.096zm-1.337 9.763H6.34v-8.59H3.667v8.59zM17.668 1H2.328C1.595 1 1 1.581 1 2.298v15.403C1 18.418 1.595 19 2.328 19h15.34c.734 0 1.332-.582 1.332-1.299V2.298C19 1.581 18.402 1 17.668 1z" clipRule="evenodd" />
            </svg>
            LinkedIn
          </a>
          <button 
            onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
            className="bg-green-600 hover:bg-green-700 text-black font-semibold py-3 px-8 rounded-lg transition-all duration-300 hover:scale-105 shadow-lg"
          >
            Get In Touch
          </button>
        </div>
      </div>
    </section>
  );
};

// About Section
const AboutSection = () => {
  return (
    <section id="about" className="py-20 bg-dark-navy">
      <div className="container mx-auto px-4">
        <div className="flex flex-col lg:flex-row items-start gap-12">
          <div>
            <h2 className="text-4xl font-bold text-white mb-8 text-center lg:text-left">About Me</h2>
            <p className="text-lg text-gray-300 mb-6 leading-relaxed">
              I am a U.S. Navy veteran and Senior Scrum Master with experience guiding technical teams in both enterprise and startup environments. My work centers on improving delivery systems so teams can move forward with clarity, consistency, and trust.
            </p>
            <p className="text-lg text-gray-300 mb-6 leading-relaxed">
              At Charles Schwab I supported multiple Scrum teams while coordinating releases and cross-team initiatives across large mobile platforms. The work required balancing Agile practices with the realities of governance, risk management, and enterprise scale.
            </p>
            <p className="text-lg text-gray-300 mb-8 leading-relaxed">
              With a diverse background spanning military service, quality assurance, and
              agile coaching, I bring a wealth of experience to help teams connect, grow,
              and achieve sustainable performance. My philosophy is simple: <strong className="text-baby-blue">"Delivery leadership across enterprise and startup environments"</strong> -
              leveraging technology to amplify human potential while keeping relationships and collaboration at the heart of everything I do.
            </p>

            <div className="bg-navy-900 p-6 rounded-lg border border-navy-700">
              <h3 className="text-xl font-bold text-baby-blue mb-4">Key Achievements</h3>
              <div className="space-y-3">
                <div className="flex items-start">
                  <span className="text-baby-blue mr-3 mt-1">★</span>
                  <span className="text-gray-300">Led 3 major mobile app integrations at Charles Schwab</span>
                </div>
                <div className="flex items-start">
                  <span className="text-baby-blue mr-3 mt-1">★</span>
                  <span className="text-gray-300">Reduced development cycles from 4 to 2 weeks</span>
                </div>
                <div className="flex items-start">
                  <span className="text-baby-blue mr-3 mt-1">★</span>
                  <span className="text-gray-300">Onboarded 20+ new team members successfully</span>
                </div>
                <div className="flex items-start">
                  <span className="text-baby-blue mr-3 mt-1">★</span>
                  <span className="text-gray-300">PMP plus multiple Agile certifications</span>
                </div>
                <div className="flex items-start">
                  <span className="text-baby-blue mr-3 mt-1">★</span>
                  <span className="text-gray-300">Eagle Scout & Black Sash in Shaolin Do Kung Fu</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Skills Section
const SkillsSection = () => {
  const certifications = [
    "Certified Scrum Master",
    "Certified Scrum Product Owner",
    "Advanced Certified Scrum Master",
    "SAFe 6 Scrum Master",
    "SAFe 6 Product Owner/Product Manager",
    "SAFe 6 Release Train Engineer",
    "Certified Agile Leader 1",
    "AI for Scrum Masters Microcredential",
    "Project Management Professional (PMP)"
  ];

  const skills = [
    { category: "Delivery Scaled Agile Coordination", items: ["Team facilitation", "Stakeholder communication", "Dependency management", "Delivery transparency"] },
    { category: "Scaled Agile Coordination", items: ["PI Planning", "Cross-team alignment", "Program execution", "Inspect and Adapt"] },
    { category: "Release & Initiative Management", items: ["Production release coordination", "Deployment readiness", "Risk tracking", "Executive status visibility"] },
    { category: "AI-Enabled Workflow Design", items: ["Structured AI prompting for documentation and analysis", "Human-in-the-loop workflow design and automation concepts", "Evaluation of AI use within governed delivery environments", "Responsible AI practices supporting team communication and planning"] },
    { category: "Continuous Improvement", items: ["Retrospectives", "Metrics interpretation", "Workflow refinement", "Onboarding support"] },
    { category: "Project & Program Management", items: ["PMP-based planning, scope control, and delivery governance", "Initiative tracking across cost, time, and scope constraints", "Executive status communication and decision support", "Risk identification, mitigation planning, and escalation"] }
  ];

  return (
    <section id="skills" className="py-20 bg-navy-900">
      <div className="container mx-auto px-4">
        <h2 className="text-4xl font-bold text-white mb-12 text-center">Professional Certifications, Continuing Education, and Professional Development</h2>
        
        <div className="grid lg:grid-cols-2 gap-12 mb-16">
          {/* Certifications */}
          <div>
            <div className="grid gap-4">
              {certifications.map((cert) => (
                <div key={cert} className="bg-dark-navy p-4 rounded-lg border border-navy-700 hover:border-baby-blue/50 transition-colors">
                  <div className="flex items-center">
                    <span className="text-baby-blue mr-3 text-lg">🏆</span>
                    <span className="text-gray-300 font-medium">{cert}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Professional Development */}
          <div>
            <div className="mb-8">
              <h4 className="text-xl font-semibold text-baby-blue mb-6">Recent Completions</h4>
              
              <div className="bg-dark-navy p-6 rounded-lg border border-navy-700 mb-6">
                <div className="flex items-start mb-4">
                  <span className="text-3xl mr-4">🎓</span>
                  <div>
                    <h5 className="text-lg font-bold text-white mb-2">AI Prompting Executive Learning Program</h5>
                    <p className="text-baby-blue font-medium mb-1">University of Texas Executive Education</p>
                    <p className="text-gray-400 text-sm mb-3">McCombs School of Business</p>
                    <p className="text-gray-300 text-sm leading-relaxed mb-4">
                      5-week program focused on mastering prompt design with ChatGPT to generate content, analyze data, and improve workflows. The program emphasized refining prompts, building custom GPTs, and applying AI skills to boost creativity, productivity, and decision-making.
                    </p>
                    <a 
                      href="https://www.credential.net/ff1e9a4b-e05d-4c81-a9e2-fb7461931a4c#acc.7XoFEtxY"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-baby-blue hover:text-white text-sm underline transition-colors"
                    >
                      View Credential →
                    </a>
                  </div>
                </div>
              </div>

              <div className="bg-dark-navy p-6 rounded-lg border border-navy-700 mb-6">
                <div className="flex items-start mb-4">
                  <span className="text-3xl mr-4">🤖</span>
                  <div>
                    <h5 className="text-lg font-bold text-white mb-2">AI Mastermind Intensive</h5>
                    <p className="text-baby-blue font-medium mb-3">Outskill Training</p>
                    <p className="text-gray-300 text-sm leading-relaxed mb-4">
                      20-hour intensive program over 3 days focusing on practical AI applications, prompt engineering, and AI integration strategies for business optimization.
                    </p>
                    <a 
                      href="https://customer-assets.emergentagent.com/job_delivery-master-4/artifacts/hc4zb1zx_Christopher_Hanten_Certificate.pdf"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-baby-blue hover:text-white text-sm underline transition-colors"
                    >
                      View Certificate →
                    </a>
                  </div>
                </div>
              </div>

              <div className="bg-dark-navy p-6 rounded-lg border border-navy-700 mb-6">
                <div className="flex items-start mb-4">
                  <span className="text-3xl mr-4">📋</span>
                  <div>
                    <h5 className="text-lg font-bold text-white mb-2">AI Automation for Business</h5>
                    <p className="text-baby-blue font-medium mb-3">Louisiana State University</p>
                    <p className="text-gray-300 text-sm leading-relaxed mb-4">
                      Completed coursework focused on practical AI automation concepts and business workflow applications.
                    </p>
                    <a 
                      href="https://customer-assets.emergentagent.com/job_ai-driven-profile/artifacts/0aa14dlc_image.png"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-baby-blue hover:text-white text-sm underline transition-colors"
                    >
                      View Certificate →
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Skills */}
        <div>
          <h3 className="text-2xl font-bold text-white mb-8 text-center">Core Competencies</h3>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {skills.map((skillGroup) => (
              <div key={skillGroup.category} className="bg-dark-navy p-6 rounded-lg border border-navy-700 hover:border-baby-blue/50 transition-colors">
                <h4 className="text-lg font-bold text-baby-blue mb-4">{skillGroup.category}</h4>
                <ul className="space-y-2">
                  {skillGroup.items.map((skill) => (
                    <li key={skill} className="text-gray-300 text-sm flex items-start">
                      <span className="text-baby-blue mr-2 mt-1 text-xs">•</span>
                      {skill}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

// Experience Section
const ExperienceSection = () => {
  const [showPreviousExperience, setShowPreviousExperience] = useState(false);

  const experiences = [
    {
      title: "Senior Scrum Master",
      company: "Charles Schwab – Mobile Technology",
      period: "Feb 2020 – May 2025",
      achievements: [
        "Supported 2 to 5 Scrum teams delivering mobile and middleware capabilities",
        "Supported major initiatives integrating independent services into Schwab flagship retail mobile experiences",
        "Built role-based onboarding guides used to successfully integrate more than 20 new team members",
        "Served as Release Coordinator for Workplace Retirement, Schwab Advisor Center, and Schwab retail mobile applications",
        "Maintained change management compliance and supported deployment audit readiness",
        "Led cross-team planning activities including PI Planning, pre-PI workshops, and Inspect and Adapt sessions",
        "Strengthened team culture through accountability, transparency, and collaborative problem solving"
      ]
    },
    {
      title: "Scrum Master",
      company: "AllClear ID",
      period: "June 2018 – Dec 2019",
      achievements: [
        "Led a 10-person cross-functional team delivering a new identity protection product",
        "Clarified user stories, acceptance criteria, and backlog priorities with stakeholders",
        "Reduced sprint cycles from four weeks to two weeks while maintaining delivery quality and predictability",
        "Partnered with project management to re-scope and sequence backlog priorities, shortening the delivery schedule by 9 weeks",
        "Supported QA across testing environments and production readiness activities",
        "Coordinated delivery requests from outside teams and supported execution clarity",
        "Managed more than 25 midnight production releases per year"
      ]
    },
    {
      title: "Software Quality Analyst",
      company: "AllClear ID",
      period: "March 2015 - October 2017",
      achievements: [
        "Authored more than 150 manual test cases across four major test suites",
        "Created testing scenarios across telephony, web services, microservices, and mobile applications",
        "Documented complete test plans for four major projects",
        "Served as SME on expected front-end behavior during feature planning and releases",
        "Represented QA during strategic planning for a monolith-to-microservices transition",
        "Created production validation test cases used regularly by Customer Support staff",
        "Improved defect reporting and tracking processes to support production SLA expectations"
      ]
    }
  ];

  const previousExperiences = [
    {
      title: "Business Operations Manager",
      company: "AllClear ID",
      period: "Apr 2014 - Dec 2014 · 9 mos",
      achievements: [
        "Managed client fulfillment team in all aspects to their customer communications",
        "Verified that team was abiding by the heavy government regulations place on our industry",
        "Reorganized the team to achieve better throughput and accuracy",
        "Created updated job requirements and interviewed candidates",
        "Led continuous improvement efforts for all processes and people in the Operations organization",
        "Was the point-of-contact with third party vendors",
        "Worked with Finance to verify correct client billing and payments"
      ]
    },
    {
      title: "Special Projects Manager",
      company: "AllClear ID",
      period: "Mar 2010 - Apr 2014 · 4 yrs 1 mo",
      achievements: [
        "Acted as primary liaison between Customer Support, Engineering, Databases Administrators, Product, and Marketing",
        "Created, with the engineering team, a brand-new process for sending out telephone-based alerts (our primary tangible product) to our customers",
        "Worked directly with our CTO on improvements and process reviews",
        "Trained our Support organization on new features, functions, and upgrades",
        "Coordinated with Product and Marketing to validate that new initiatives made sense from a user perspective",
        "This was a primarily unsupervised position, reporting directly to VP of Customer Services",
        "Acted as a liaison between Customer Support, Engineering, and Databases Administrators to take bugs and nice-to-haves from the support team to the back-end team",
        "Worked heavily with Marketing to make sure that new initiatives made sense from a user perspective"
      ]
    },
    {
      title: "Customer Services Manager",
      company: "AllClear ID",
      period: "Sep 2007 - Mar 2010 · 2 yrs 6 mos",
      achievements: [
        "Created and enforced workflow protocols to provide smooth operations",
        "Provided strong organizational leadership and crisis management through exemplary behavior, remaining calm during customer crises and quick to offer corrective solutions based on years of support experience",
        "Identified opportunities for improvement by assessing reports and monitoring calls and vendor relationships",
        "Ensured all agents were up-to-date with current products, services and troubleshooting knowledge and collaborated with cross-functional departments, including sales and product management, QA, and sales and marketing",
        "Discovered and resolved complex customer issues negatively affecting business outcomes",
        "Maximized productivity by supervising, mentoring and scheduling team personnel",
        "Directed personnel training and mentored team members to promote productivity, accuracy and commitment to friendly service"
      ]
    }
  ];

  return (
    <section id="experience" className="py-20 bg-dark-navy">
      <div className="container mx-auto px-4">
        <h2 className="text-4xl font-bold text-white mb-12 text-center">Professional Experience</h2>
        <div className="space-y-8">
          {experiences.map((exp) => (
            <div key={`${exp.company}-${exp.title}`} className="bg-navy-900 p-8 rounded-lg border border-navy-700 hover:border-baby-blue/50 transition-colors">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6">
                <div>
                  <h3 className="text-2xl font-bold text-white mb-2">{exp.title}</h3>
                  <h4 className="text-xl text-baby-blue font-medium">{exp.company}</h4>
                </div>
                <span className="text-gray-400 font-medium mt-2 md:mt-0">{exp.period}</span>
              </div>
              <div className="space-y-3">
                {exp.achievements.map((achievement) => (
                  <div key={achievement} className="flex items-start">
                    <span className="text-baby-blue mr-3 mt-1">•</span>
                    <span className="text-gray-300">{achievement}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Previous Experience Toggle */}
        <div className="mt-12 text-center">
          <button
            onClick={() => setShowPreviousExperience(!showPreviousExperience)}
            className="inline-flex items-center text-baby-blue hover:text-white transition-colors font-medium text-lg"
          >
            <span className="mr-2">View Previous Professional Experience</span>
            <svg 
              className={`w-5 h-5 transition-transform duration-300 ${showPreviousExperience ? 'rotate-180' : ''}`}
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
        </div>

        {/* Previous Experience Section */}
        {showPreviousExperience && (
          <div className="mt-8 space-y-8 animate-fadeIn">
            <h3 className="text-3xl font-bold text-white text-center mb-8">Previous Professional Experience</h3>
            {previousExperiences.map((exp) => (
              <div key={`${exp.company}-${exp.title}`} className="bg-navy-900/70 p-8 rounded-lg border border-navy-700/70 hover:border-baby-blue/50 transition-colors">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6">
                  <div>
                    <h4 className="text-2xl font-bold text-white mb-2">{exp.title}</h4>
                    <h5 className="text-xl text-baby-blue font-medium">{exp.company}</h5>
                  </div>
                  <span className="text-gray-400 font-medium mt-2 md:mt-0">{exp.period}</span>
                </div>
                <div className="space-y-3">
                  {exp.achievements.map((achievement) => (
                    <div key={achievement} className="flex items-start">
                      <span className="text-baby-blue mr-3 mt-1">•</span>
                      <span className="text-gray-300">{achievement}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

// Projects Section
const ProjectsSection = () => {
  const projects = [
    {
      title: "Schwab Mobile App Integration",
      description: "Supported the integration of multiple independent services into the Schwab retail mobile platform, improving cross-team coordination and release readiness.",
      technologies: ["Mobile Development", "Release Management", "Cross-team Coordination"],
      impact: "Improved cross-product consistency and delivery coordination"
    },
    {
      title: "SAFe Transformation Initiative",
      description: "Introduced PI Planning and coached teams on scaled Agile practices to strengthen alignment and predictability.",
      technologies: ["SAFe Framework", "PI Planning", "Agile Coaching"],
      impact: "Strengthened alignment and delivery predictability across teams"
    },
    {
      title: "Development Cycle Optimization",
      description: "Reduced development cycle length through backlog refinement and process adjustments while maintaining delivery quality.",
      technologies: ["Release & Initiative Management", "Backlog Management", "Team Optimization"],
      impact: "50 percent reduction in development cycle time"
    },
    {
      title: "Team Onboarding Program",
      description: "Designed structured onboarding documentation and support processes for Product Owners, Developers, and QA.",
      technologies: ["Documentation", "Training Programs", "Process Design"],
      impact: "Helped more than 20 new team members ramp effectively"
    }
  ];

  return (
    <section id="projects" className="py-20 bg-navy-900">
      <div className="container mx-auto px-4">
        <h2 className="text-4xl font-bold text-white mb-12 text-center">Representative Initiatives</h2>
        <div className="grid md:grid-cols-2 gap-8">
          {projects.map((project) => (
            <div key={project.title} className="bg-dark-navy p-8 rounded-lg border border-navy-700 hover:border-baby-blue/50 transition-all duration-300 hover:scale-105">
              <h3 className="text-2xl font-bold text-white mb-4">{project.title}</h3>
              <p className="text-gray-300 mb-6 leading-relaxed">{project.description}</p>
              
              <div className="mb-6">
                <h4 className="font-semibold text-baby-blue mb-3">Key Areas:</h4>
                <div className="flex flex-wrap gap-2">
                  {project.technologies.map((tech) => (
                    <span key={tech} className="bg-baby-blue/20 text-baby-blue px-3 py-1 rounded-full text-sm font-medium">
                      {tech}
                    </span>
                  ))}
                </div>
              </div>

              <div>
                <h4 className="font-semibold text-baby-blue mb-2">Impact:</h4>
                <p className="text-gray-300 text-sm">{project.impact}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Contact Section (for home page)
const ContactSection = () => {
  return (
    <section id="contact" className="py-20 bg-dark-navy">
      <div className="container mx-auto px-4">
        <h2 className="text-4xl font-bold text-white mb-8 text-center">Get In Touch</h2>
        
        {/* Contact Information */}
        <div className="text-center mb-12">
          <h3 className="text-3xl font-bold text-white mb-8">Let's Connect</h3>
          <p className="text-gray-300 mb-12 text-lg max-w-2xl mx-auto">
            I'm open to conversations about Scrum Master, delivery leadership, and AI-enabled workflow support roles, along with thoughtful discussions about team health, continuous improvement, and practical ways of working.
          </p>
        </div>
        
        <div className="grid md:grid-cols-3 gap-8">
          <div className="bg-navy-900 p-6 rounded-lg border border-navy-700 hover:border-baby-blue/50 transition-colors text-center">
            <div className="w-12 h-12 bg-baby-blue/20 rounded-lg flex items-center justify-center mx-auto mb-4">
              <span className="text-2xl">📞</span>
            </div>
            <h4 className="text-xl font-bold text-white mb-3">Phone</h4>
            <a href="tel:(737)210-6954" className="text-baby-blue hover:text-white transition-colors">
              (737) 210-6954
            </a>
          </div>

          <div className="bg-navy-900 p-6 rounded-lg border border-navy-700 hover:border-baby-blue/50 transition-colors text-center">
            <div className="w-12 h-12 bg-baby-blue/20 rounded-lg flex items-center justify-center mx-auto mb-4">
              <span className="text-2xl">📧</span>
            </div>
            <h4 className="text-xl font-bold text-white mb-3">Email</h4>
            <a href="mailto:hanten.christopher@gmail.com" className="text-baby-blue hover:text-white transition-colors break-all">
              hanten.christopher@gmail.com
            </a>
          </div>

          <div className="bg-navy-900 p-6 rounded-lg border border-navy-700 hover:border-baby-blue/50 transition-colors text-center">
            <div className="w-12 h-12 bg-baby-blue/20 rounded-lg flex items-center justify-center mx-auto mb-4">
              <span className="text-2xl">💼</span>
            </div>
            <h4 className="text-xl font-bold text-white mb-3">LinkedIn</h4>
            <a 
              href="https://www.linkedin.com/in/christopherhanten" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-baby-blue hover:text-white transition-colors"
            >
              Connect with me
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

// Footer
const Footer = () => {
  return (
    <footer className="bg-navy-900 border-t border-navy-700 py-8">
      <div className="container mx-auto px-4 text-center">
        <p className="text-gray-400">
          © 2026 Christopher Hanten. All rights reserved.
        </p>
      </div>
    </footer>
  );
};

// Home Page Component
const HomePage = () => {
  useEffect(() => {
    // Test API connection
    const testApi = async () => {
      try {
        const response = await axios.get(`${API}/`);
        console.log(response.data.message);
      } catch (e) {
        console.error('API connection error:', e);
      }
    };
    testApi();
  }, []);

  return (
    <>
      <HeroSection />
      <AboutSection />
      <SkillsSection />
      <ExperienceSection />
      <ProjectsSection />
      <ContactSection />
      <Footer />
    </>
  );
};

// AI Portfolio Page
const AIPage = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-dark-navy via-navy-900 to-navy-800 text-white">
      <div className="container mx-auto px-4 py-32">
        <h1 className="text-4xl md:text-6xl font-bold mb-8 text-center bg-gradient-to-r from-white to-baby-blue bg-clip-text text-transparent">
          AI & Workflow Thinking
        </h1>
        
        <div className="max-w-6xl mx-auto">
          {/* Meta-demonstration Notice */}
          <div className="bg-navy-900/30 border border-baby-blue/30 rounded-lg p-2 mb-8">
            <div className="text-center">
              <p className="text-gray-300 leading-relaxed">
                This entire portfolio web app is a <span className="text-baby-blue font-medium">meta-demonstration</span>, developed through collaborative prompting and "vibe coding"—with examples of the AI integration skills showcased below. Each component, from navigation to embedded viewers, emerged from strategic conversation with AI tools, embodying the <strong className="text-white">"People first; AI-driven"</strong> approach in practice.
              </p>
            </div>
          </div>

          {/* Introduction Section */}
          <div className="text-center mb-16">
            <div className="max-w-md mx-auto mb-8">
              <img 
                src="https://images.unsplash.com/photo-1593377202145-c5e97fd065f4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzF8MHwxfHNlYXJjaHwxfHxyb2JvdCUyMGFzc2lzdGFudHxlbnwwfHx8fDE3NTY4NDUzMjJ8MA&ixlib=rb-4.1.0&q=85"
                alt="AI Robot Assistant"
                className="w-full h-48 object-cover rounded-lg mx-auto"
              />
            </div>
            <h2 className="text-3xl font-bold text-white mb-4">🤖 AI-Powered Solutions</h2>
            <p className="text-baby-blue text-lg mb-6">
              Practical AI applications with measurable business impact
            </p>
            <p className="text-gray-300 mb-8 leading-relaxed max-w-3xl mx-auto">
              This portfolio showcases AI-powered projects, automation solutions, and marketing campaigns 
              built on the foundation of <strong className="text-baby-blue">"People first; AI-driven"</strong> - 
              where technology serves to elevate human collaboration and creativity, not replace it.
            </p>
          </div>

          {/* Featured Projects Section */}
          <div className="mb-16">
            <h3 className="text-3xl font-bold text-white mb-8 text-center">Featured Projects</h3>
            
            <div className="grid grid-cols-1 gap-12">
              {/* EcoStride Project Card */}
              <div className="bg-navy-900/50 p-8 rounded-lg border border-navy-700 hover:border-baby-blue/50 transition-all duration-300">
                <div className="flex flex-col gap-8">
                  {/* Project Header */}
                  <div className="flex items-start">
                    <div className="mr-4">
                      <div className="w-16 h-16 bg-baby-blue/20 rounded-lg flex items-center justify-center">
                        <span className="text-3xl">🦶</span>
                      </div>
                    </div>
                    <div className="flex-1">
                      <h4 className="text-2xl font-bold text-white mb-2">EcoStride AI-Driven Marketing Campaign</h4>
                      <p className="text-baby-blue font-medium mb-3">Sustainable Footwear Brand Launch Strategy</p>
                      <div className="flex flex-wrap gap-2 mb-4">
                        <span className="bg-baby-blue/20 text-baby-blue px-3 py-1 rounded-full text-sm font-medium">ChatGPT</span>
                        <span className="bg-baby-blue/20 text-baby-blue px-3 py-1 rounded-full text-sm font-medium">Gamma</span>
                        <span className="bg-baby-blue/20 text-baby-blue px-3 py-1 rounded-full text-sm font-medium">Google Docs</span>
                        <span className="bg-baby-blue/20 text-baby-blue px-3 py-1 rounded-full text-sm font-medium">Gemini</span>
                        <span className="bg-baby-blue/20 text-baby-blue px-3 py-1 rounded-full text-sm font-medium">AI Prompting</span>
                        <span className="bg-baby-blue/20 text-baby-blue px-3 py-1 rounded-full text-sm font-medium">Marketing Strategy</span>
                      </div>
                    </div>
                  </div>

                  {/* PDF Viewer Section */}
                  <div className="bg-dark-navy p-6 rounded-lg border border-navy-700">
                    <h5 className="font-semibold text-white mb-4 flex items-center">
                      <span className="text-baby-blue mr-2">📄</span>
                      View Complete Presentation
                    </h5>
                    <div className="bg-white rounded-lg overflow-hidden shadow-lg">
                      <iframe
                        src="https://drive.google.com/file/d/1bavVKoJ4dun76tnGko9lM14lNtrWgXMa/preview"
                        className="w-full h-96 border-0"
                        title="EcoStride AI Marketing Campaign Presentation"
                        allow="autoplay"
                      ></iframe>
                    </div>
                    <div className="mt-4 text-center">
                      <a 
                        href="https://drive.google.com/file/d/1bavVKoJ4dun76tnGko9lM14lNtrWgXMa/view?usp=sharing"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-baby-blue hover:text-white transition-colors text-sm underline"
                      >
                        Open Full Presentation in New Tab
                      </a>
                    </div>
                  </div>

                  {/* Project Details */}
                  <div className="space-y-4">
                    <div>
                      <h5 className="font-semibold text-gray-200 mb-2">🎯 Challenge</h5>
                      <p className="text-gray-300">
                        Created a comprehensive marketing campaign for EcoStride, a sustainable footwear brand targeting environmentally conscious consumers. 
                        Developed brand positioning, messaging strategy, and multi-channel campaign approach using collaborative AI workflows.
                      </p>
                    </div>
                    
                    <div>
                      <h5 className="font-semibold text-gray-200 mb-2">🔧 Multi-AI Tool Strategy</h5>
                      <p className="text-gray-300">
                        Leveraged ChatGPT for strategic messaging and content creation, Gamma for presentation design, Google Docs for collaboration, 
                        and Gemini for market analysis. This multi-tool approach ensured comprehensive coverage while maintaining brand consistency 
                        and strategic alignment.
                      </p>
                    </div>

                    <div>
                      <h5 className="font-semibold text-gray-200 mb-2">📈 Strategic Impact & Methodology</h5>
                      <p className="text-gray-300">
                        Delivered complete brand launch package including positioning strategy, campaign creative, and implementation roadmap. 
                        <strong className="text-baby-blue">Demonstrates expertise in AI-powered strategic thinking and cross-platform content development.</strong> 
                        Each component reinforced the core message while reaching different customer segments effectively.
                      </p>
                    </div>
                  </div>

                  <div className="border-t border-navy-700 pt-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-gray-400">
                          <span className="text-baby-blue font-medium">Strategic Project</span> | 
                          Brand Launch & Multi-Channel Marketing
                        </p>
                      </div>
                      <div className="text-baby-blue text-sm font-medium">
                        Sustainability × Performance × AI
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* GreenScape Project Card */}
              <div className="bg-navy-900/50 p-8 rounded-lg border border-navy-700 hover:border-baby-blue/50 transition-all duration-300">
                <div className="flex flex-col gap-8">
                  {/* Project Header */}
                  <div className="flex items-start">
                    <div className="mr-4">
                      <div className="w-16 h-16 bg-baby-blue/20 rounded-lg flex items-center justify-center">
                        <span className="text-3xl">💧</span>
                      </div>
                    </div>
                    <div className="flex-1">
                      <h4 className="text-2xl font-bold text-white mb-2">GreenScape Solutions Smart Irrigation System Launch Package</h4>
                      <p className="text-baby-blue font-medium mb-3">Multi-Stakeholder Communication Strategy for Smart IoT Technology</p>
                      <div className="flex flex-wrap gap-2 mb-4">
                        <span className="bg-baby-blue/20 text-baby-blue px-3 py-1 rounded-full text-sm font-medium">ChatGPT</span>
                        <span className="bg-baby-blue/20 text-baby-blue px-3 py-1 rounded-full text-sm font-medium">Chronicle</span>
                        <span className="bg-baby-blue/20 text-baby-blue px-3 py-1 rounded-full text-sm font-medium">Google Docs</span>
                        <span className="bg-baby-blue/20 text-baby-blue px-3 py-1 rounded-full text-sm font-medium">Gemini</span>
                        <span className="bg-baby-blue/20 text-baby-blue px-3 py-1 rounded-full text-sm font-medium">IoT Strategy</span>
                      </div>
                    </div>
                  </div>

                  {/* Content Section */}
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Document Viewer */}
                    <div className="bg-dark-navy p-6 rounded-lg border border-navy-700">
                      <h5 className="font-semibold text-white mb-4 flex items-center">
                        <span className="text-baby-blue mr-2">📋</span>
                        Strategy Document
                      </h5>
                      <div className="bg-white rounded-lg overflow-hidden shadow-lg">
                        <iframe
                          src="https://drive.google.com/file/d/1lsIZGXUS2YHmuxuVxjbkKjNei1IITXhH/preview"
                          className="w-full h-80 border-0"
                          title="GreenScape Solutions Strategy Document"
                          allow="autoplay"
                        ></iframe>
                      </div>
                      <div className="mt-4 text-center">
                        <a 
                          href="https://drive.google.com/file/d/1lsIZGXUS2YHmuxuVxjbkKjNei1IITXhH/view?usp=sharing"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-baby-blue hover:text-white transition-colors text-sm underline"
                        >
                          Open Strategy Document in New Tab
                        </a>
                      </div>
                    </div>

                    {/* Presentation Viewer */}
                    <div className="bg-dark-navy p-6 rounded-lg border border-navy-700">
                      <h5 className="font-semibold text-white mb-4 flex items-center">
                        <span className="text-baby-blue mr-2">📊</span>
                        Executive Presentation
                      </h5>
                      <div className="bg-white rounded-lg overflow-hidden shadow-lg">
                        <iframe
                          src="https://drive.google.com/file/d/1qv1cZ9Z409oFvPTqHlGb1J7uP3CBPw37/preview"
                          className="w-full h-80 border-0"
                          title="GreenScape Solutions Executive Presentation"
                          allow="autoplay"
                        ></iframe>
                      </div>
                      <div className="mt-4 text-center">
                        <a 
                          href="https://drive.google.com/file/d/1qv1cZ9Z409oFvPTqHlGb1J7uP3CBPw37/view?usp=sharing"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-baby-blue hover:text-white transition-colors text-sm underline"
                        >
                          Open Executive Presentation in New Tab
                        </a>
                      </div>
                    </div>
                  </div>

                  {/* Project Details */}
                  <div className="space-y-4">
                    <div>
                      <h5 className="font-semibold text-gray-200 mb-2">🎯 Challenge</h5>
                      <p className="text-gray-300">
                        Created comprehensive launch package for GreenScape's smart irrigation system that "listens before it acts" - 
                        using soil sensors and weather data to optimize watering. Needed to communicate complex IoT technology 
                        to diverse audiences: Executive Board, Cross-Functional Teams, and City Partners.
                      </p>
                    </div>
                    
                    <div>
                      <h5 className="font-semibold text-gray-200 mb-2">🔧 Multi-AI Tool Strategy</h5>
                      <p className="text-gray-300">
                        Employed ChatGPT for strategic messaging, Gemini for technical analysis, Google Docs for collaborative 
                        documentation, and Chronicle for executive presentations. This multi-tool approach ensured tailored 
                        communication while maintaining technical accuracy and strategic alignment across all stakeholder groups.
                      </p>
                    </div>

                    <div>
                      <h5 className="font-semibold text-gray-200 mb-2">📈 Multi-Stakeholder Delivery</h5>
                      <p className="text-gray-300">
                        Delivered both strategic documentation and executive presentation materials, demonstrating ability to 
                        translate complex IoT solutions into compelling business cases. <strong className="text-baby-blue">
                        Shows expertise in multi-AI tool integration and stakeholder-specific communication.</strong>
                      </p>
                    </div>
                  </div>

                  <div className="border-t border-navy-700 pt-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-gray-400">
                          <span className="text-baby-blue font-medium">Advanced Project</span> | 
                          Multi-Tool AI Integration & Stakeholder Communication
                        </p>
                      </div>
                      <div className="text-baby-blue text-sm font-medium">
                        IoT × Sustainability × Multi-AI
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Agile Sprint Planning Kit Project Card */}
              <div className="bg-navy-900/50 p-8 rounded-lg border border-navy-700 hover:border-baby-blue/50 transition-all duration-300">
                <div className="flex flex-col gap-8">
                  {/* Project Header */}
                  <div className="flex items-start">
                    <div className="mr-4">
                      <div className="w-16 h-16 bg-baby-blue/20 rounded-lg flex items-center justify-center">
                        <span className="text-3xl">🚀</span>
                      </div>
                    </div>
                    <div className="flex-1">
                      <h4 className="text-2xl font-bold text-white mb-2">Agile Sprint Planning Kit and Action Pack</h4>
                      <p className="text-baby-blue font-medium mb-3">Educational Presentation Series for Agile Teams</p>
                      <div className="flex flex-wrap gap-2 mb-4">
                        <span className="bg-baby-blue/20 text-baby-blue px-3 py-1 rounded-full text-sm font-medium">ChatGPT</span>
                        <span className="bg-baby-blue/20 text-baby-blue px-3 py-1 rounded-full text-sm font-medium">Gemini</span>
                        <span className="bg-baby-blue/20 text-baby-blue px-3 py-1 rounded-full text-sm font-medium">Claude</span>
                        <span className="bg-baby-blue/20 text-baby-blue px-3 py-1 rounded-full text-sm font-medium">Gamma</span>
                        <span className="bg-baby-blue/20 text-baby-blue px-3 py-1 rounded-full text-sm font-medium">Google Docs</span>
                        <span className="bg-baby-blue/20 text-baby-blue px-3 py-1 rounded-full text-sm font-medium">Prompt Genie</span>
                        <span className="bg-baby-blue/20 text-baby-blue px-3 py-1 rounded-full text-sm font-medium">Wispr Flow</span>
                      </div>
                    </div>
                  </div>

                  {/* Content Section */}
                  <div className="grid grid-cols-1 gap-6">
                    {/* PDF Viewer Section */}
                    <div className="bg-dark-navy p-6 rounded-lg border border-navy-700">
                      <h5 className="font-semibold text-white mb-4 flex items-center">
                        <span className="text-baby-blue mr-2">📋</span>
                        Sprint Planning Kit Presentation
                      </h5>
                      <div className="bg-white rounded-lg overflow-hidden shadow-lg">
                        <iframe
                          src="https://drive.google.com/file/d/1CsnCNrnPthSGdjWjiTDJtPK2QZ1y89-N/preview"
                          className="w-full h-96 border-0"
                          title="Agile Sprint Planning Kit and Action Pack Presentation"
                          allow="autoplay"
                        ></iframe>
                      </div>
                      <div className="mt-4 text-center">
                        <a 
                          href="https://drive.google.com/file/d/1CsnCNrnPthSGdjWjiTDJtPK2QZ1y89-N/view?usp=sharing"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-baby-blue hover:text-white transition-colors text-sm underline"
                        >
                          Open Full Presentation in New Tab
                        </a>
                      </div>
                    </div>
                  </div>

                  {/* Project Details */}
                  <div className="space-y-4">
                    <div>
                      <h5 className="font-semibold text-gray-200 mb-2">🎯 Purpose & Vision</h5>
                      <p className="text-gray-300">
                        Created a comprehensive educational presentation to guide new Scrum teams through effective Agile sprint planning. 
                        This is the first installment in a planned series covering all sprint events, designed to give especially new or 
                        younger developers a solid foundation in Agile Scrum methodology and set them up for success.
                      </p>
                    </div>
                    
                    <div>
                      <h5 className="font-semibold text-gray-200 mb-2">🔧 Multi-AI Tool Strategy</h5>
                      <p className="text-gray-300">
                        Leveraged seven different AI tools to create comprehensive educational content: ChatGPT for content development, 
                        Gemini and Claude for diverse perspectives, Gamma for presentation design, Google Docs for collaboration, 
                        Prompt Genie for optimization, and Wispr Flow for workflow efficiency. This multi-tool approach ensures 
                        well-rounded, practical guidance.
                      </p>
                    </div>

                    <div>
                      <h5 className="font-semibold text-gray-200 mb-2">📈 Educational Impact & Series Vision</h5>
                      <p className="text-gray-300">
                        Provides actionable resources and clear guidance for teams new to Agile methodologies. <strong className="text-baby-blue">
                        Demonstrates expertise in knowledge transfer, team development, and systematic approach to Agile education.</strong> 
                        Sets foundation for comprehensive sprint event series to empower development teams.
                      </p>
                    </div>
                  </div>

                  <div className="border-t border-navy-700 pt-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-gray-400">
                          <span className="text-baby-blue font-medium">Educational Series</span> | 
                          Agile Team Development & Knowledge Transfer
                        </p>
                      </div>
                      <div className="text-baby-blue text-sm font-medium">
                        Agile × Education × Multi-AI
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Skills & Capabilities */}
          <div className="mb-16">
            <h3 className="text-3xl font-bold text-white mb-8 text-center">AI Capabilities</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-dark-navy p-6 rounded-lg border border-navy-700 text-center">
                <div className="w-12 h-12 bg-baby-blue/20 rounded-lg flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl">🧠</span>
                </div>
                <h4 className="text-xl font-bold text-white mb-3">Strategic AI Prompting</h4>
                <p className="text-gray-300">Designing prompts that generate actionable business content and strategic insights</p>
              </div>
              <div className="bg-dark-navy p-6 rounded-lg border border-navy-700 text-center">
                <div className="w-12 h-12 bg-baby-blue/20 rounded-lg flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl">⚡</span>
                </div>
                <h4 className="text-xl font-bold text-white mb-3">Workflow Automation</h4>
                <p className="text-gray-300">Building AI-powered systems to streamline repetitive processes and improve efficiency</p>
              </div>
              <div className="bg-dark-navy p-6 rounded-lg border border-navy-700 text-center">
                <div className="w-12 h-12 bg-baby-blue/20 rounded-lg flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl">📊</span>
                </div>
                <h4 className="text-xl font-bold text-white mb-3">Business Integration</h4>
                <p className="text-gray-300">Practical application of AI tools to solve real-world business challenges</p>
              </div>
            </div>
          </div>

          {/* Expanding Portfolio */}
          <div className="bg-navy-900/50 p-8 rounded-lg border border-navy-700">
            <h3 className="text-2xl font-bold text-white mb-6 text-center">Growing AI Portfolio</h3>
            <div className="text-center">
              <p className="text-gray-300 mb-6 leading-relaxed">
                This portfolio continues to expand as I apply AI tools to solve complex business challenges, 
                automate workflows, and enhance team productivity. Each project demonstrates practical value 
                while maintaining the human-centered approach that defines effective leadership.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-left max-w-2xl mx-auto">
                <div className="flex items-start">
                  <span className="text-baby-blue mr-3 mt-1">•</span>
                  <span className="text-gray-300">Custom GPT applications for project management</span>
                </div>
                <div className="flex items-start">
                  <span className="text-baby-blue mr-3 mt-1">•</span>
                  <span className="text-gray-300">AI-powered retrospective analysis tools</span>
                </div>
                <div className="flex items-start">
                  <span className="text-baby-blue mr-3 mt-1">•</span>
                  <span className="text-gray-300">Automated content creation workflows</span>
                </div>
                <div className="flex items-start">
                  <span className="text-baby-blue mr-3 mt-1">•</span>
                  <span className="text-gray-300">Data-driven team performance insights</span>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-12 text-center">
            <p className="text-gray-400 italic max-w-3xl mx-auto">
              "The future belongs to teams that embrace a 'People first; AI-driven' mindset - 
              I want to use sociology, Agile principles, and artificial intelligence to amplify human creativity, collaboration, and value delivery." 
              <br/><span className="text-baby-blue">- Christopher Hanten</span>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

// Contact Page Component  
const ContactPage = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-dark-navy via-navy-900 to-navy-800 text-white">
      <div className="container mx-auto px-4 py-32">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h1 className="text-4xl md:text-6xl font-bold mb-8 bg-gradient-to-r from-white to-baby-blue bg-clip-text text-transparent">
              Let's Connect
            </h1>
            <p className="text-gray-300 mb-8 text-lg">
              I'd love to talk to you about Agile transformation, using AI to streamline business operations, music, Star Wars, sneakers, or to just share some stories. If you're looking to build high-performing teams or want to explore working together, let's chat. My approach: <strong className="text-baby-blue">"People first; AI-driven"</strong> - 
              combining human-centered leadership with smart technology integration.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-12">
            {/* Contact Information */}
            <div>
              <h2 className="text-2xl font-bold text-white mb-6">Contact Information</h2>
              <div className="space-y-6">
                <div className="flex items-center">
                  <div className="w-12 h-12 bg-baby-blue/20 rounded-lg flex items-center justify-center mr-4">
                    <span className="text-xl">📍</span>
                  </div>
                  <div>
                    <h3 className="font-semibold text-white">Location</h3>
                    <p className="text-gray-300">Austin, Texas</p>
                  </div>
                </div>

                <div className="flex items-center">
                  <div className="w-12 h-12 bg-baby-blue/20 rounded-lg flex items-center justify-center mr-4">
                    <span className="text-xl">📞</span>
                  </div>
                  <div>
                    <h3 className="font-semibold text-white">Phone</h3>
                    <a href="tel:(737)210-6954" className="text-baby-blue hover:text-white transition-colors">
                      (737) 210-6954
                    </a>
                  </div>
                </div>

                <div className="flex items-center">
                  <div className="w-12 h-12 bg-baby-blue/20 rounded-lg flex items-center justify-center mr-4">
                    <span className="text-xl">📧</span>
                  </div>
                  <div>
                    <h3 className="font-semibold text-white">Email</h3>
                    <a href="mailto:hanten.christopher@gmail.com" className="text-baby-blue hover:text-white transition-colors break-all">
                      hanten.christopher@gmail.com
                    </a>
                  </div>
                </div>

                <div className="flex items-center">
                  <div className="w-12 h-12 bg-baby-blue/20 rounded-lg flex items-center justify-center mr-4">
                    <span className="text-xl">💼</span>
                  </div>
                  <div>
                    <h3 className="font-semibold text-white">LinkedIn</h3>
                    <a 
                      href="https://www.linkedin.com/in/christopherhanten" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-baby-blue hover:text-white transition-colors"
                    >
                      Connect with me
                    </a>
                  </div>
                </div>
              </div>
            </div>

            {/* Availability & Focus Areas */}
            <div>
              <h2 className="text-2xl font-bold text-white mb-6">Current Focus</h2>
              <div className="space-y-4">
                <div>
                  <h4 className="font-semibold text-baby-blue mb-2">Current Focus</h4>
                  <p className="text-gray-300">Agile transformation leadership and Scrum Master consulting</p>
                </div>
                <div>
                  <h4 className="font-semibold text-baby-blue mb-2">Experience</h4>
                  <p className="text-gray-300">5+ years as Senior Scrum Master in enterprise and startup environments</p>
                </div>
                <div>
                  <h4 className="font-semibold text-baby-blue mb-2">Specializations</h4>
                  <ul className="text-gray-300 space-y-1">
                    <li>• Cross-team coordination and release management</li>
                    <li>• SAFe framework implementation and PI Planning</li>
                    <li>• Team onboarding and process improvement</li>
                    <li>• AI integration for delivery workflows</li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-semibold text-baby-blue mb-2">Open to Discussing</h4>
                  <ul className="text-gray-300 space-y-1">
                    <li>• Agile transformation opportunities</li>
                    <li>• Scrum Master consulting engagements</li>
                    <li>• Speaking at events or conferences</li>
                    <li>• Collaborative projects and partnerships</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Other Interests Page Component
const OtherInterestsPage = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-dark-navy via-navy-900 to-navy-800 text-white">
      <div className="container mx-auto px-4 py-32">
        <div className="text-center">
          <h1 className="text-4xl md:text-6xl font-bold mb-8 bg-gradient-to-r from-white to-blue bg-clip-text text-transparent">
            Other Interests
          </h1>
          <div className="max-w-2xl mx-auto">
            <img 
              src="https://images.unsplash.com/photo-1581833971358-2c8b550f87b3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzF8MHwxfHNlYXJjaHw5fHx2aW50YWdlJTIwY29uc3RydWN0aW9ufGVufDB8fHx8MTc1Njg0NTI5Mnww&ixlib=rb-4.1.0&q=85"
              alt="Under Construction - Vintage Style"
              className="w-full h-64 object-cover rounded-lg mb-8"
            />
            <p className="text-xl text-gray-300 mb-6">
              This section is currently under construction. 
            </p>
            <p className="text-gray-400">
              Check back soon for updates on my personal interests, hobbies, and projects outside of work!
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

// Main App Component
const App = () => {
  return (
    <BrowserRouter>
      <div className="App">
        <Navigation />
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/ai" element={<AIPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/other-interests" element={<OtherInterestsPage />} />
        </Routes>
      </div>
    </BrowserRouter>
  );
};

export default App;